import { getSessionCookieData, isGokollab } from '@ghl/clientportal-core/init'
import { RouteLocationNormalized, Router } from 'vue-router'

import { GroupState } from '@/composition/groups'
import { navigation } from '@/composition/navigation'
import { UserState } from '@/composition/users'
import { emitter } from '@/main'
import { MemberStatus } from '@/models/Groups'
import { useGlobalStore } from '@/store/app'
import {
  buildLoginRedirectQuery,
  withInternalRedirectQuery,
} from '@/util/redirect'
import { AnalyticsService } from '@ghl/clientportal-core'

/**
 * Wait for group data to finish loading
 * @param timeout - Maximum time to wait in milliseconds (default: 10000)
 * @returns Promise that resolves when fetchingGroupData is false and role/userStatus are set
 */
async function waitForGroupDataToLoad(timeout = 10000): Promise<void> {
  return new Promise((resolve, _reject) => {
    const startTime = Date.now()

    const checkGroupData = () => {
      // If fetchingGroupData is false, we're done
      if (!GroupState.state.fetchingGroupData && !GroupState.state.fetchingUserGroup) {
        resolve()
        return
      }

      // Check if we've exceeded the timeout
      if (Date.now() - startTime > timeout) {
        // eslint-disable-next-line no-console
        console.warn('Timeout waiting for group data to load')
        resolve() // Resolve anyway to prevent infinite waiting
        return
      }

      // Check again in 100ms
      setTimeout(checkGroupData, 100)
    }

    checkGroupData()
  })
}

// Routes to be allowed on private groups with groupslug. [Routes without groupslug are allowed by default]
const allowRoutesOnPrivateGroup = [
  'NotificationMobileView',
  'clientportal-home',
  'profile-container',
  'communities-more',
  'conversations',
  'chat-view',
  'documents-and-payments',
]

export class RouterHelper {
  static async waitForGroupDataToLoad(timeout = 10000): Promise<void> {
    return waitForGroupDataToLoad(timeout)
  }

  static async handleInactiveGroups(route: RouteLocationNormalized, router: Router): Promise<void> {
    // Check if we're navigating to a group route
    if (route.params?.groupSlug) {
      // Wait for group data to load before making routing decisions
      await this.waitForGroupDataToLoad()
    }

    if (route.params?.groupSlug && GroupState.state.groupData?.status === 'Inactive' && route.name !== 'inactive-group') {
      await router.push({
        name: 'inactive-group',
        params: { groupSlug: route.params.groupSlug },
      })
      return
    }

    if (route.name === 'inactive-group' && GroupState.state.groupData?.status && GroupState.state.groupData?.status !== 'Inactive') {
      await router.push({
        name: 'home',
        params: { groupSlug: route.params.groupSlug },
      })
    }
  }

  static async handlePrivateGroups(route: RouteLocationNormalized, router: Router, routeFrom?: RouteLocationNormalized): Promise<void> {
    // Skip private group checks for my-courses route
    if (route.name === 'my-courses') {
      return
    }

    // Check if we're navigating to a group route
    if (route.params?.groupSlug) {
      // Wait for group data to load before making routing decisions
      await this.waitForGroupDataToLoad()
    }

    // Skip private group logic if group is inactive (inactive takes priority)
    if (GroupState.state.groupData?.status === 'Inactive') {
      return
    }

    if (
      route.name === 'private-group' &&
      GroupState.state.groupData?.settings?.groupType !== 'Private' &&
      GroupState.state.groupData?.settings?.groupType !== undefined
    ) {
      await router.push({
        name: 'home',
        params: { groupSlug: route.params.groupSlug },
        query: route.query,
      })
      return
    }
    if (
      GroupState.state.groupData?.settings?.groupType === 'Private' &&
      route.name !== 'private-group' &&
      route?.params?.groupSlug &&
      allowRoutesOnPrivateGroup.indexOf(route.name as string) === -1
    ) {
      if (!GroupState.state?.role && route.name !== 'home') {
        emitter.emit('access:private-group-unauthorized', {
          routeName: String(route.name),
          message: 'Join the group to access this section',
        })
      }

      if (!GroupState.state?.role || GroupState.state?.userStatus !== MemberStatus.ACTIVE) {
        const appStore = useGlobalStore()
        const gateQuery =
          appStore.isAuthenticated === false &&
          route.name !== 'private-group' &&
          route.name !== 'home'
            ? withInternalRedirectQuery({ ...route.query, ...buildLoginRedirectQuery(route.fullPath) })
            : route.query
        router.push({
          name: 'private-group',
          params: { groupSlug: route.params.groupSlug },
          query: gateQuery,
        })
        const configuredRoute = navigation.state.navigationItems.filter(item => {
          return item.routeName.includes(route.name as string)
        })
        if (routeFrom?.path && configuredRoute && configuredRoute.length > 0 && routeFrom?.params?.groupSlug === route?.params?.groupSlug) {
          emitter.emit('route-change:show-access-information')
        }
      }
    }
    if (route.name === 'private-group' && GroupState.state?.role && GroupState.state.userStatus === MemberStatus.ACTIVE) {
      await router.push({
        name: 'home',
        params: { groupSlug: route.params.groupSlug },
        query: route.query,
      })
    }
  }

  static async handleBannedUser(route: RouteLocationNormalized, router: Router): Promise<void> {
    // Check if we're navigating to a group route
    if (route.params?.groupSlug) {
      // Wait for group data to load before making routing decisions
      await this.waitForGroupDataToLoad()
    }

    const slug = GroupState?.state?.groupData?.slug
    if (
      GroupState?.state?.userStatus === MemberStatus.BANNED &&
      route.name !== 'content-unavailable' &&
      slug &&
      decodeURIComponent(route.path).includes(slug)
    ) {
      await router.push({ name: 'content-unavailable' })
    }
  }

  static async sendPendoTrackingEvent(route: RouteLocationNormalized): Promise<void> {
    // Check if we're navigating to a group route
    if (route.params?.groupSlug) {
      // Wait for group data to load before making routing decisions
      await this.waitForGroupDataToLoad()
    }

    let locationId = GroupState.state.groupData?.locationId
    if (!locationId) {
      const sessionCookieData = getSessionCookieData()
      locationId = sessionCookieData?.locationId
    }
    if (locationId) {
      const contactId = getSessionCookieData()?.contactId
      AnalyticsService.trackPageViewEvents({
        locationId: locationId,
        eventType: 'communites_user_page_hit',
        route: route.path,
        ...(contactId && { contactId }),
      }).catch(error => {
        console.error('Failed to send tracking event:', error)
      })
      AnalyticsService.trackPageViewEvents({
        locationId: locationId,
        eventType: 'clientportal_user_page_hit',
        route: route.path,
        ...(contactId && { contactId }),
      }).catch(error => {
        console.error('Failed to send tracking event:', error)
      })
      if (isGokollab()) {
        AnalyticsService.trackPageViewEvents({
          locationId: locationId,
          eventType: 'gokollab_user_page_hit',
          route: route.path,
          ...(contactId && { contactId }),
        }).catch(error => {
          console.error('Failed to send tracking event:', error)
        })
      }
    }
  }

  static async checkAndShowMandatoryInfoModel(route?: RouteLocationNormalized): Promise<void> {
    UserState.setMandatoryInfoModal(false)
    if (route?.params?.groupSlug) {
      // Wait for group data to load before making routing decisions
      await this.waitForGroupDataToLoad()
    }
    const mandatoryInfoPresent = UserState.checkMandatedInfoPresentOrNot()
    UserState.setMandatoryInfoModal(!mandatoryInfoPresent)
  }
}
